import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useProducts } from "../hooks/useProducts";

const INCLUDED_FEATURES = [
  "Unlimited AI summaries, quizzes & flashcards",
  "Every subject and difficulty level",
  "Voice practice & detailed answer analysis",
  "Progress tracking & personalized insights",
  "Synced across iPhone and the web",
  "Priority customer support",
];

const PricingSection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { loading, error, monthlyProduct, yearlyProduct, savingsPercentage } =
    useProducts();
  const [activePlan, setActivePlan] = useState<"monthly" | "yearly">("yearly");

  const getBuyNowUrl = (originalUrl: string) => {
    if (!user?.email) return originalUrl;

    const separator = originalUrl.includes("?") ? "&" : "?";
    return `${originalUrl}${separator}checkout[email]=${encodeURIComponent(
      user.email,
    )}&checkout[name]=${encodeURIComponent(
      user.displayName || "",
    )}&checkout[custom][firebase_uid]=${encodeURIComponent(user.uid)}`;
  };

  const activeProduct =
    activePlan === "monthly" ? monthlyProduct : yearlyProduct;

  const perMonthPrice =
    activePlan === "monthly"
      ? activeProduct?.attributes.price_formatted.replace("/month", "")
      : activeProduct
        ? `$${(
            Math.floor((activeProduct.attributes.price / 100 / 12) * 100) / 100
          ).toFixed(2)}`
        : "";

  return (
    <section
      id="pricing"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 flex justify-center">
        <div className="w-[700px] h-[400px] bg-gradient-to-b from-blue-50 to-transparent rounded-full opacity-80 blur-2xl" />
      </div>

      <div className="container mx-auto px-5 md:px-8 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.p
            className="text-xs font-semibold uppercase tracking-wider text-[#47b3fe] mb-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Pricing
          </motion.p>
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            One plan. Everything unlocked.
          </motion.h2>
          <motion.p
            className="text-slate-500 mt-3 max-w-md mx-auto"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Start learning smarter today. Cancel anytime — no questions asked.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left — included features */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-xl font-semibold text-slate-900 mb-5 tracking-tight">
              Everything you need to learn faster
            </h3>
            <ul className="space-y-3.5">
              {INCLUDED_FEATURES.map((feature, i) => (
                <motion.li
                  key={i}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.07 }}
                >
                  <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-[#47b3fe] to-indigo-600 flex items-center justify-center">
                    <i className="fas fa-check text-white text-[10px]"></i>
                  </span>
                  <span className="text-slate-600 leading-relaxed">
                    {feature}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right — pricing card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            {/* Gradient ring backdrop */}
            <div className="absolute -inset-px rounded-[26px] bg-gradient-to-br from-[#47b3fe]/40 via-indigo-400/30 to-purple-400/30 blur-[2px]" />

            <div className="relative bg-white rounded-[24px] border border-slate-100 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] p-7 md:p-8 overflow-hidden">
              {/* Corner glow */}
              <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 bg-[#47b3fe]/10 rounded-full blur-2xl" />

              {/* Toggle */}
              <div className="flex justify-center mb-7">
                <div className="bg-slate-100 rounded-full p-1 flex items-center">
                  <button
                    onClick={() => setActivePlan("monthly")}
                    className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                      activePlan === "monthly"
                        ? "text-white"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {activePlan === "monthly" && (
                      <motion.div
                        layoutId="pricingPlanIndicator"
                        className="absolute inset-0 bg-gradient-to-r from-[#47b3fe] to-indigo-600 rounded-full"
                        initial={false}
                        transition={{ type: "spring", duration: 0.5 }}
                      />
                    )}
                    <span className="relative z-10">Monthly</span>
                  </button>
                  <button
                    onClick={() => setActivePlan("yearly")}
                    className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                      activePlan === "yearly"
                        ? "text-white"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {activePlan === "yearly" && (
                      <motion.div
                        layoutId="pricingPlanIndicator"
                        className="absolute inset-0 bg-gradient-to-r from-[#47b3fe] to-indigo-600 rounded-full"
                        initial={false}
                        transition={{ type: "spring", duration: 0.5 }}
                      />
                    )}
                    <span className="relative z-10">Yearly</span>
                    {savingsPercentage > 0 && (
                      <span className="absolute -top-2.5 -right-2 z-10 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                        -{savingsPercentage}%
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-12 w-40 bg-slate-200 rounded-lg mx-auto" />
                  <div className="h-4 w-32 bg-slate-100 rounded mx-auto" />
                  <div className="h-14 w-full bg-slate-200 rounded-xl mt-6" />
                  <div className="h-3 w-48 bg-slate-100 rounded mx-auto" />
                </div>
              ) : error || !activeProduct ? (
                <div className="text-center py-6">
                  <i className="fas fa-circle-exclamation text-slate-300 text-2xl mb-3" />
                  <p className="text-slate-500 text-sm">
                    Pricing is temporarily unavailable.
                  </p>
                  <a
                    href="https://apps.apple.com/us/app/einstein-ai-learn-memorize/id6741355940"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-4 text-[#47b3fe] font-semibold text-sm hover:underline"
                  >
                    View plans in the app →
                  </a>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activePlan}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="text-center"
                  >
                    {/* Price */}
                    <div className="flex items-end justify-center gap-1.5">
                      <span className="text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#47b3fe] to-indigo-600 tracking-tight">
                        {perMonthPrice}
                      </span>
                      <span className="text-slate-400 mb-1.5 font-medium">
                        / month
                      </span>
                    </div>

                    {/* Billing detail */}
                    <div className="mt-2 h-6 flex items-center justify-center">
                      {activePlan === "yearly" ? (
                        <p className="text-sm text-slate-500">
                          Billed annually at{" "}
                          <span className="font-semibold text-slate-700">
                            {activeProduct.attributes.price_formatted}
                          </span>
                        </p>
                      ) : (
                        <p className="text-sm text-slate-500">
                          Billed monthly, cancel anytime
                        </p>
                      )}
                    </div>

                    {/* Savings banner — fixed slot so card height stays constant */}
                    <div className="mt-4 h-9 flex items-center justify-center">
                      {activePlan === "yearly" && savingsPercentage > 0 && (
                        <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm font-medium rounded-full px-4 py-1.5">
                          <i className="fas fa-piggy-bank text-green-500" />
                          Save {savingsPercentage}% vs. monthly
                        </div>
                      )}
                    </div>

                    {/* CTA — logged in goes straight to checkout (email prefilled);
                        logged out signs in first so the checkout email matches the account */}
                    {user ? (
                      <a
                        href={getBuyNowUrl(
                          activeProduct.attributes.buy_now_url,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-7 w-full px-6 py-4 bg-gradient-to-r from-[#47b3fe] to-indigo-600 text-white rounded-xl font-semibold
                                   shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all duration-200
                                   flex items-center justify-center gap-2"
                      >
                        Start Learning Now
                        <i className="fas fa-arrow-right text-sm" />
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          navigate("/login", {
                            state: {
                              checkoutUrl: activeProduct.attributes.buy_now_url,
                            },
                          })
                        }
                        className="mt-7 w-full px-6 py-4 bg-gradient-to-r from-[#47b3fe] to-indigo-600 text-white rounded-xl font-semibold
                                   shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all duration-200
                                   flex items-center justify-center gap-2"
                      >
                        Start Learning Now
                        <i className="fas fa-arrow-right text-sm" />
                      </button>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
